var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { findCup, useASTDispatchAction, getNextPath, resetPathLastIndex, } from '@saucerjs/core';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import shortid from 'shortid';
import { CupItemSymbol, TeaItemSymbol } from '../symbol';
/**
 * render wrapper dnd event hooks.
 */
export function useDragAndDrop(props) {
    var path = props.path, tea = props.tea, canDrop = props.canDrop;
    var teaType = tea.type;
    var isRoot = path === '';
    var _a = useASTDispatchAction(), dispatchInsertBefore = _a.dispatchInsertBefore, dispatchInsertAfter = _a.dispatchInsertAfter, dispatchAppendChildren = _a.dispatchAppendChildren, dispatchMoveNodeByPath = _a.dispatchMoveNodeByPath;
    var ref = useRef(null);
    var _b = useState('bottom'), hoverDirection = _b[0], setHoverDirection = _b[1];
    var handleDrop = useCallback(function (item) {
        var dropType = item.type;
        if (dropType === CupItemSymbol) {
            // Create new tea
            var cupName = item.name;
            var cup = findCup(cupName);
            if (cup === null) {
                console.error('Cannot find cup by name:' + cupName);
                return;
            }
            var nodeId = typeof cup.generateNodeId === 'function'
                ? cup.generateNodeId()
                : shortid();
            var nodeAttrs = {};
            if (typeof cup.defaultAttrs !== 'undefined') {
                var defaultAttrs = typeof cup.defaultAttrs === 'function'
                    ? cup.defaultAttrs({ nodeId: nodeId })
                    : cup.defaultAttrs;
                nodeAttrs = __assign({}, defaultAttrs);
            }
            if (teaType === 'container') {
                dispatchAppendChildren(path, cup.type, nodeId, cup.name, nodeAttrs);
            }
            else {
                if (hoverDirection === 'top') {
                    dispatchInsertBefore(path, cup.type, nodeId, cup.name, nodeAttrs);
                }
                else {
                    dispatchInsertAfter(path, cup.type, nodeId, cup.name, nodeAttrs);
                }
            }
        }
        else if (dropType === TeaItemSymbol) {
            // Move existed tea
            var fromPath = item.path;
            var toPath = path;
            if (teaType === 'container') {
                // Move into container
                var subLastPath = resetPathLastIndex(toPath + ".0", tea.children.length);
                if (subLastPath.startsWith(fromPath)) {
                    // Move into Self
                    // Ignore it
                    return;
                }
                dispatchMoveNodeByPath(fromPath, subLastPath);
            }
            else {
                if (hoverDirection === 'top') {
                    dispatchMoveNodeByPath(fromPath, toPath);
                }
                else {
                    dispatchMoveNodeByPath(fromPath, getNextPath(toPath));
                }
            }
        }
    }, [
        teaType,
        path,
        hoverDirection,
        dispatchInsertBefore,
        dispatchInsertAfter,
        dispatchAppendChildren,
        dispatchMoveNodeByPath,
    ]);
    var _c = useDrag({
        item: { type: TeaItemSymbol, path: path },
        collect: function (monitor) { return ({
            opacity: monitor.isDragging() ? 0.5 : 1,
        }); },
    }), opacity = _c[0].opacity, dragRef = _c[1];
    var _d = useDrop({
        accept: [CupItemSymbol, TeaItemSymbol],
        drop: function (item, monitor) {
            var didDrop = monitor.didDrop();
            if (didDrop) {
                // Skip if any one handle this event.
                return;
            }
            handleDrop(item);
        },
        hover: function (item, monitor) {
            var _a;
            // Determine rectangle on screen
            var hoverBoundingRect = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (!hoverBoundingRect) {
                return;
            }
            // Get vertical middle
            var hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            // Determine mouse position
            var clientOffset = monitor.getClientOffset();
            if (clientOffset === null) {
                return;
            }
            // Get pixels to the top
            var hoverClientY = clientOffset.y - hoverBoundingRect.top;
            var direction = hoverClientY > hoverMiddleY ? 'bottom' : 'top';
            setHoverDirection(direction);
        },
        collect: function (monitor) {
            var _a;
            return ({
                isOverCurrent: monitor.isOver({ shallow: true }),
                dragName: (_a = monitor.getItem()) === null || _a === void 0 ? void 0 : _a.name,
            });
        },
        canDrop: function () { return canDrop; },
    }), _e = _d[0], isOverCurrent = _e.isOverCurrent, dragName = _e.dragName, dropRef = _d[1];
    var dndRef = isRoot ? dropRef(ref) : dragRef(dropRef(ref));
    var dndClassName = useMemo(function () {
        if (!isOverCurrent) {
            return '';
        }
        if (teaType !== 'leaf') {
            return '';
        }
        return "saucer-render-wrapper__hover-" + hoverDirection;
    }, [isOverCurrent, hoverDirection]);
    var dndStyle = { opacity: opacity };
    return { dndRef: dndRef, isOverCurrent: isOverCurrent, dragName: dragName, dndClassName: dndClassName, dndStyle: dndStyle };
}
